import React, { useEffect, useState } from "react";
import { openWhatsApp } from "../../lib/whatsapp";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Key, Plus, Trash2, MessageCircle, RefreshCw, AlertTriangle, ShieldCheck,
  Users, Eye, EyeOff, RotateCcw, Phone, Save, X, History,
  BedDouble, LogIn, LogOut, Home, ChevronDown, ChevronUp, UserPlus, ShieldAlert, Smartphone,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TTLOCK_ANDROID_PKG = "com.tongtongsuo.app";
const TTLOCK_IOS_STORE = "https://apps.apple.com/app/id1033046018";
const TTLOCK_WEB = "https://lock2.ttlock.com";

// TTLock has no public per-lock deep link: open the app (or store) and copy
// the lock name so the admin can find it in one tap.
export function openTTLockApp(lockName) {
  try { navigator.clipboard?.writeText(lockName || ""); } catch { /* silent */ }
  toast.info(`Cerca "${lockName}" nell'app TTLock`, { description: "Nome serratura copiato negli appunti" });
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) {
    const fallback = encodeURIComponent(`https://play.google.com/store/apps/details?id=${TTLOCK_ANDROID_PKG}`);
    window.location.href = `intent://open#Intent;package=${TTLOCK_ANDROID_PKG};S.browser_fallback_url=${fallback};end`;
  } else if (/iphone|ipad|ipod/i.test(ua)) {
    const t0 = Date.now();
    window.location.href = "ttlock://";
    setTimeout(() => { if (Date.now() - t0 < 2200 && !document.hidden) window.location.href = TTLOCK_IOS_STORE; }, 1500);
  } else {
    window.open(TTLOCK_WEB, "_blank", "noopener");
  }
}

// Alignment between dashboard PINs and the physical lock (set by reconcile).
export function lockSyncState(reservations) {
  const withPin = (reservations || []).filter((r) => r.ttlock_pin);
  const checked = withPin.filter((r) => typeof r.ttlock_verified === "boolean");
  const lastAt = checked.map((r) => r.ttlock_verified_at).filter(Boolean).sort().pop() || null;
  const fmt = lastAt ? new Date(lastAt).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
  if (!withPin.length || !checked.length) {
    return { state: "unknown", dot: "bg-neutral-400", label: "Non verificato", title: withPin.length ? "In attesa del prossimo reconcile" : "Nessun PIN attivo" };
  }
  if (checked.some((r) => r.ttlock_verified === false)) {
    return { state: "misaligned", dot: "bg-red-500", label: "Disallineato", title: `PIN nel gestionale non presente sulla serratura · verificato ${fmt}` };
  }
  return { state: "aligned", dot: "bg-emerald-500", label: "Allineato", title: `Tutti i PIN presenti sulla serratura · verificato ${fmt}` };
}

function client() {
  const tok = localStorage.getItem("admin_token");
  return axios.create({
    baseURL: API,
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
}

export default function LocksEditor() {
  const [status, setStatus] = useState(null);
  const [locks, setLocks] = useState([]);
  const [pins, setPins] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    guest_name: "", guest_phone: "", room_id: "",
    lock_id: "", start_iso: "", end_iso: "", keyboard_pwd: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const s = await client().get("/ttlock/status");
      setStatus(s.data);
      const requests = [client().get("/ttlock/pins"), client().get("/ttlock/staff")];
      if (s.data.configured) requests.push(client().get("/ttlock/locks"));
      const results = await Promise.all(requests);
      setPins(results[0].data.pins || []);
      setStaff(results[1].data.staff || []);
      if (s.data.configured) setLocks(results[2].data.list || []);
    } catch (e) {
      toast.error("Errore caricamento TTLock");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createPin = async () => {
    if (!form.guest_name || !form.lock_id || !form.start_iso || !form.end_iso) {
      toast.error("Compila nome, camera, date di inizio e fine");
      return;
    }
    setCreating(true);
    try {
      const { data } = await client().post("/ttlock/pins", {
        guest_name: form.guest_name,
        guest_phone: form.guest_phone,
        room_id: form.room_id,
        lock_id: Number(form.lock_id),
        start_iso: new Date(form.start_iso).toISOString(),
        end_iso: new Date(form.end_iso).toISOString(),
        keyboard_pwd: form.keyboard_pwd || null,
      });
      toast.success(`PIN creato: ${data.keyboard_pwd}`);
      setForm({ guest_name: "", guest_phone: "", room_id: "", lock_id: "", start_iso: "", end_iso: "", keyboard_pwd: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.ttlock?.errmsg || e.response?.data?.detail || "Errore creazione PIN");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (pin) => {
    if (!confirm(`Revocare PIN ${pin.keyboard_pwd} di ${pin.guest_name}?`)) return;
    try {
      await client().delete(`/ttlock/pins/${pin.id}`);
      toast.success("PIN revocato");
      load();
    } catch (e) {
      toast.error("Revoca fallita");
    }
  };

  const sendWhatsApp = async (pin, who) => {
    try {
      const { data } = await client().post(`/ttlock/pins/${pin.id}/notify`);
      const url = who === "guest" ? data.guest_wa_url : data.admin_wa_url;
      openWhatsApp(url);
    } catch (e) {
      toast.error("Errore generazione link WhatsApp");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="locks-editor">
      {/* Status banner */}
      <div className="rounded-2xl bg-gradient-to-br from-[#2b1410] to-[#4a1c17] text-[#f7ebdf] p-5 flex flex-wrap items-center gap-4">
        <div className="h-11 w-11 rounded-lg bg-[#f7ebdf]/10 flex items-center justify-center">
          <Key className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-serif text-2xl font-bold">Serrature Camere — TTLock</h2>
          <p className="text-sm text-[#f7ebdf]/75 mt-0.5">
            Crea un PIN 4 cifre per ogni prenotazione. Il codice si attiva al check-in e scade al check-out.
          </p>
        </div>
        {status?.configured ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/30 border border-emerald-400/30 px-3 py-1 text-xs font-semibold">
            <ShieldCheck className="h-3.5 w-3.5" /> Collegato · {status.count_locks} serrature
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-600/30 border border-amber-400/30 px-3 py-1 text-xs font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" /> Non configurato
          </span>
        )}
        <button onClick={load} className="inline-flex items-center gap-1 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 border border-[#f7ebdf]/20 px-3 py-1.5 text-xs">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
        </button>
      </div>

      {status?.configured && status?.writes_allowed === false && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-900 p-4 text-sm" data-testid="ttlock-readonly-banner">
          <div className="font-bold mb-1">Anteprima: scritture TTLock bloccate</div>
          Da qui puoi solo leggere le serrature (Sincronizza, Storico). Genera / Rigenera / Elimina / Pulisci agiscono sulle serrature reali e funzionano solo dal sito pubblicato.
        </div>
      )}

      {!status?.configured && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm">
          <div className="font-bold mb-1">Credenziali TTLock mancanti</div>
          Aggiungi in <code>backend/.env</code>: <code>TTLOCK_CLIENT_ID</code>, <code>TTLOCK_CLIENT_SECRET</code>,
          <code>TTLOCK_USERNAME</code>, <code>TTLOCK_PASSWORD</code>. Poi riavvia il backend.
          <br /><br />
          <b>Come ottenerle:</b> attendi l'approvazione della tua app su <code>euopen.ttlock.com</code> (stato "In reviewing"). Alla conferma riceverai Client ID + Secret. Username/password sono quelli dell'account che possiede le 7 serrature (<code>lock2.ttlock.com</code>).
        </div>
      )}

      {/* Create form */}
      <div className="rounded-2xl border border-[#e8dcc8] bg-white p-5 space-y-3">
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Nuovo PIN prenotazione</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <FormField label="Nome ospite *">
            <input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" data-testid="lock-guest-name" />
          </FormField>
          <FormField label="Telefono ospite (con prefisso)">
            <input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} placeholder="+39 333 1234567" className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" data-testid="lock-guest-phone" />
          </FormField>
          <FormField label="Camera / etichetta">
            <input value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })} placeholder="Argo" className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" />
          </FormField>
          <FormField label="Serratura *">
            <select value={form.lock_id} onChange={(e) => setForm({ ...form, lock_id: e.target.value })} className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm bg-white" data-testid="lock-select">
              <option value="">— seleziona —</option>
              {locks.map((l) => (
                <option key={l.lockId} value={l.lockId}>{l.lockAlias || l.lockName} (#{l.lockId})</option>
              ))}
            </select>
            {!status?.configured && <span className="text-[11px] text-amber-700">Disponibile dopo la configurazione</span>}
          </FormField>
          <FormField label="Check-in *">
            <input type="datetime-local" value={form.start_iso} onChange={(e) => setForm({ ...form, start_iso: e.target.value })} className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" />
          </FormField>
          <FormField label="Check-out *">
            <input type="datetime-local" value={form.end_iso} onChange={(e) => setForm({ ...form, end_iso: e.target.value })} className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" />
          </FormField>
          <FormField label="PIN (vuoto = generato)">
            <input value={form.keyboard_pwd} onChange={(e) => setForm({ ...form, keyboard_pwd: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })} placeholder="4 cifre" className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" />
          </FormField>
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={createPin} disabled={creating || !status?.configured} data-testid="lock-create-pin" className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2 text-sm font-semibold disabled:opacity-50">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Crea PIN e sincronizza serratura
          </button>
        </div>
      </div>

      {/* Active PINs */}
      <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#e8dcc8]">
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">PIN attivi ({pins.filter(p => p.status === "active").length})</h3>
        </div>
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
        ) : pins.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#7c5a4e]">Nessun PIN creato</div>
        ) : (
          <div className="divide-y divide-[#e8dcc8]">
            {pins.map((p) => (
              <div key={p.id} data-testid={`lock-pin-${p.id}`} className={`px-4 py-3 flex flex-wrap items-center gap-3 ${p.status !== "active" ? "opacity-50" : ""}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-[#2b1410]">{p.guest_name} <span className="text-xs text-[#7c5a4e] font-normal">· {p.room_id || `Lock ${p.lock_id}`}</span></div>
                  <div className="text-xs text-[#7c5a4e]">
                    {new Date(p.start_iso).toLocaleString("it-IT")} → {new Date(p.end_iso).toLocaleString("it-IT")}
                  </div>
                </div>
                <div className="font-mono text-lg font-bold text-[#4a1c17] tracking-widest">{p.keyboard_pwd}</div>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-700"}`}>{p.status}</span>
                {p.status === "active" && (
                  <>
                    <button onClick={() => sendWhatsApp(p, "guest")} className="inline-flex items-center gap-1 rounded-full bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs font-semibold" data-testid={`lock-wa-guest-${p.id}`}>
                      <MessageCircle className="h-3.5 w-3.5" /> Ospite
                    </button>
                    <button onClick={() => sendWhatsApp(p, "admin")} className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-3 py-1 text-xs font-semibold">
                      <MessageCircle className="h-3.5 w-3.5" /> Me
                    </button>
                    <button onClick={() => revoke(p)} className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-700 hover:bg-red-50 px-3 py-1 text-xs">
                      <Trash2 className="h-3.5 w-3.5" /> Revoca
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Staff — codici permanenti */}
      <StaffPanel staff={staff} configured={!!status?.configured} onChange={load} />

      {/* Codici Prenotazioni Ospiti (da Housekeeping) */}
      <ReservationPinsPanel />

      {/* Serrature Spa (Tuya) */}
      <SpaLocksPanel />
    </div>
  );
}

function ReservationPinsPanel() {
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [purgeStatus, setPurgeStatus] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, roomsRes] = await Promise.all([
        client().get("/hk/reservations"),
        client().get("/hk/rooms").catch(() => ({ data: [] })),
      ]);
      setRows(rRes.data || []);
      setRooms(roomsRes.data || []);
    } catch (e) {
      // silent — panel just stays empty
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Live purge status polling — every 8s if a purge is running.
  useEffect(() => {
    let mounted = true;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await client().get("/hk/ttlock/purge-status");
        if (!mounted) return;
        setPurgeStatus(data);
        if (data.status === "running") {
          timer = setTimeout(poll, 8000);
        }
      } catch {
        // silent
      }
    };
    poll();
    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, []);

  // Keep only reservations from today onwards (checkout ≥ today).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const activeRows = rows.filter((r) => {
    try { return new Date(r.end) >= today; } catch { return true; }
  });

  const search = q.trim().toLowerCase();
  const matchesQ = (r) => !search
    || (r.summary || "").toLowerCase().includes(search)
    || (r.guest_phone || "").includes(search);

  // Group by room, sorted by check-in date.
  const byRoom = (roomId) => activeRows
    .filter((r) => r.room_id === roomId)
    .sort((a, b) => (a.start || "").localeCompare(b.start || ""));

  const filteredRooms = rooms.filter((room) => {
    if (!search) return true;
    if (room.name?.toLowerCase().includes(search)) return true;
    return byRoom(room.id).some(matchesQ);
  });

  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden" data-testid="reservation-pins-panel">
      {/* Live TTLock purge status banner */}
      {purgeStatus && purgeStatus.status && purgeStatus.status !== "never" && (
        <div className={`px-5 py-2 text-xs flex items-center gap-2 border-b border-[#e8dcc8]
          ${purgeStatus.status === "running" ? "bg-amber-50 text-amber-800"
            : purgeStatus.status === "done" ? "bg-emerald-50 text-emerald-800"
            : purgeStatus.status === "error" ? "bg-red-50 text-red-800"
            : "bg-stone-50 text-stone-700"}`}
          data-testid="ttlock-purge-status-banner"
        >
          {purgeStatus.status === "running" && (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Pulizia archivi TTLock in corso… (whitelist: {purgeStatus.keep_count} codici protetti)</span>
              <span className="ml-auto text-stone-500">start {new Date(purgeStatus.started_at).toLocaleTimeString("it-IT")}</span>
            </>
          )}
          {purgeStatus.status === "done" && (
            <>
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>
                Pulizia TTLock completata: {(purgeStatus.report?.revoked ?? 0)} revocati, {(purgeStatus.report?.failed ?? 0)} falliti.
                {" "}Ora puoi rigenerare i PIN futuri.
              </span>
              <button
                className="ml-auto underline hover:no-underline"
                onClick={() => setPurgeStatus({ status: "never" })}
              >
                nascondi
              </button>
            </>
          )}
          {purgeStatus.status === "error" && (
            <>
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Pulizia TTLock in errore: {(purgeStatus.error || "").slice(0, 200)}</span>
            </>
          )}
          {purgeStatus.status === "interrupted" && (
            <>
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Pulizia TTLock interrotta (backend riavviato). Rilancia il bottone rosso "Pulisci archivi TTLock".</span>
            </>
          )}
        </div>
      )}

      <div className="px-5 py-3 border-b border-[#e8dcc8] flex items-center gap-2 flex-wrap">
        <Key className="h-5 w-5 text-[#4a1c17]" />
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Codici Prenotazioni Ospiti</h3>
        <span className="text-[11px] text-[#7c5a4e] hidden sm:inline">Una card per stanza · genera PIN e invia WhatsApp</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca ospite / camera / tel…"
          className="ml-auto rounded-full border border-[#e8dcc8] px-3 py-1 text-xs w-full sm:w-56"
          data-testid="reservation-pins-search"
        />
        <button onClick={load} className="rounded-full border border-[#e8dcc8] px-3 py-1 text-xs inline-flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
        </button>
        <button
          onClick={() => setShowAdd((v) => !v)}
          data-testid="reservation-add-toggle"
          className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"
        >
          <UserPlus className="h-3.5 w-3.5" /> {showAdd ? "Chiudi" : "Aggiungi prenotazione"}
        </button>
        <button
          onClick={async () => {
            if (reconciling) return;
            setReconciling(true);
            try {
              const { data } = await client().post("/hk/ttlock/reconcile");
              if (data.error) { toast.error(`Sincronizzazione impossibile: ${data.error}`); await load(); return; }
              const lockErrors = (data.locks || []).filter((l) => l.error).map((l) => `camera ${l.room_number}: ${l.error}`);
              if (lockErrors.length) toast.error(`TTLock non risponde per ${lockErrors.length} serrature`, { description: lockErrors.join(" · "), duration: 12000 });
              const parts = [];
              if (data.updated > 0) parts.push(`${data.updated} codici aggiornati`);
              if (data.misaligned > 0) parts.push(`${data.misaligned} PIN NON presenti sulla serratura`);
              if (data.duplicates > 0) parts.push(`${data.duplicates} ospiti con codice doppio`);
              const dupNames = (data.locks || []).flatMap((l) => (l.duplicates || []).map((d) => `${d.guest} (${d.db_pin} + ${d.extra_pin})`));
              if (data.misaligned > 0 || data.duplicates > 0) {
                toast.warning(`Sincronizzazione completata: ${parts.join(" · ")}`, {
                  description: dupNames.length ? `Codici doppi su TTLock: ${dupNames.join("; ")}. Il gestionale mantiene il primo, elimina l'altro dall'app TTLock.` : undefined,
                  duration: 12000,
                });
              } else {
                toast.success(parts.length ? `Sincronizzazione completata: ${parts.join(" · ")}` : "Sincronizzazione completata — tutti i PIN allineati con TTLock");
              }
              await load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore sincronizzazione");
            } finally {
              setReconciling(false);
            }
          }}
          disabled={reconciling}
          data-testid="reconcile-ttlock-btn"
          className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
          title="Legge i codici davvero attivi sulle serrature e allinea il DB. Zero rischi — non tocca TTLock."
        >
          {reconciling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {reconciling ? "Lettura TTLock… (30-60s)" : "Sincronizza da TTLock"}
        </button>
        <button
          onClick={async () => {
            try {
              const [{ data }, auditRes] = await Promise.all([
                client().get("/hk/ttlock/actions?limit=150"),
                client().get("/hk/ttlock/audit?limit=150").catch(() => ({ data: { rows: [] } })),
              ]);
              const actions = (data.actions || []).map((a) => ({ ...a, _t: a.timestamp, _src: "action" }));
              const audit = (auditRes.data?.events || auditRes.data?.rows || []);
              const auditRows = (Array.isArray(audit) ? audit : []).map((a) => ({ ...a, _t: a.created_at, _src: "audit", action: a.type }));
              const rows = [...actions, ...auditRows].sort((x, y) => String(y._t).localeCompare(String(x._t)));
              const esc = (s) => String(s ?? "").replace(/</g, "&lt;");
              const detailsList = (d) => (Array.isArray(d) ? d.map((x) => `${x.room ? `cam ${x.room} ` : ""}${esc(x.guest || "")} pin=${esc(x.pin || "")}`).join(" | ") : "");
              const html =
                `<div style="max-height:70vh;overflow:auto;font-family:monospace;font-size:12px">` +
                rows.map((a) => {
                  const t = new Date(a._t).toLocaleString("it-IT");
                  const act = a.action || "";
                  const danger = /revoke|delete|cleanup|orphan|pin_link_lost|disappeared/.test(act);
                  const detail =
                    act === "wa_send"
                      ? `pin=<b>${esc(a.pin_sent)}</b> guest=${esc(a.guest)} action=${esc(a.pin_action)}`
                      : act === "reconcile_update"
                      ? `${esc(a.pin_before)} → <b>${esc(a.pin_after)}</b> guest=${esc(a.guest)} (${esc(a.match_method)})`
                      : act === "sync_disappeared_detected" || act === "pin_link_lost"
                      ? `count=${a.count} — ${detailsList(a.details)}`
                      : act === "nightly_cleanup_ok" || act === "nightly_cleanup_failed"
                      ? `<b>PULIZIA NOTTURNA</b> codice ${esc(a.name)} pin=<b>${esc(a.pin)}</b> scaduto il ${a.end_date_ms ? new Date(a.end_date_ms).toLocaleString("it-IT") : "?"}`
                      : act === "revoke_ok" || act === "revoke_failed"
                      ? `<b>CODICE ELIMINATO DALLA SERRATURA</b> pin=<b>${esc(a.pin)}</b> guest=${esc(a.guest)} ${a.error ? "err=" + esc(a.error) : ""}`
                      : act === "pren_id_rotation_rescued"
                      ? `PIN mantenuto ${esc(a.kept_pin)} guest=${esc(a.guest)} (${esc(a.match || "identity")})`
                      : act === "auto_rotate_prevented"
                      ? `nome cambiato ${esc(a.prev_summary)} → ${esc(a.new_summary)} — PIN ${esc(a.existing_pin)} mantenuto`
                      : esc(JSON.stringify(a)).slice(0, 220);
                  return `<div style="padding:4px 6px;border-bottom:1px solid #eee;${danger ? "background:#fdecec" : ""}"><b style="color:#4a1c17">${t}</b> · [${esc(act)}] room=${esc(a.room_number ?? "-")} ${detail}</div>`;
                }).join("") + `</div>`;
              const w = window.open("", "history", "width=1000,height=650");
              w.document.write(`<html><head><title>Storico TTLock — ${rows.length} eventi</title></head><body style="padding:12px;margin:0;background:#faf3ee"><p style="font:12px sans-serif;color:#7c5a4e;margin:0 0 8px">In rosso: eventi che hanno toccato o perso codici. Orari locali.</p>${html}</body></html>`);
            } catch (e) {
              toast.error("Errore caricamento storico");
            }
          }}
          data-testid="ttlock-history-btn"
          className="rounded-full border border-[#e8dcc8] text-[#7c5a4e] hover:bg-[#f8f1e5] px-3 py-1 text-xs inline-flex items-center gap-1"
          title="Storico azioni: WhatsApp inviati, codici cambiati, reconcile"
        >
          <History className="h-3.5 w-3.5" /> Storico
        </button>
        <button
          onClick={async () => {
            // Preview: count future reservations (excluding in-house).
            const todayS = new Date().toISOString().split("T")[0];
            const futureCount = rows.filter((r) => (r.start || "") > todayS).length;
            const inHouseCount = rows.filter((r) => {
              const s = r.start || ""; const e = r.end || "";
              return s <= todayS && e >= todayS;
            }).length;
            if (futureCount === 0) {
              toast.info("Nessuna prenotazione futura da rigenerare.");
              return;
            }
            if (!confirm(
              `Rigenerare ${futureCount} PIN degli arrivi futuri?\n\n` +
              `• ${inHouseCount} ospiti attualmente IN CASA saranno saltati (i loro PIN NON cambiano)\n` +
              `• ${futureCount} codici futuri esistenti verranno revocati su TTLock e rigenerati puliti\n` +
              `• I nuovi codici sono a 6 cifre con prefisso 00 (validi dalle 14:00 del check-in)\n` +
              `• Dovrai reinviare i nuovi PIN via WhatsApp (il flag "già inviato" verrà resettato)\n\n` +
              `L'operazione richiede ~${Math.ceil(futureCount * 4 / 60)} minuti in background.`
            )) return;
            try {
              const { data } = await client().post("/hk/reservations/regen-all-future");
              toast.success(`Rigenerazione avviata su ${data.queued} PIN. Saltati ${data.skipped_in_house.length} ospiti in casa.`);
              setTimeout(load, 5000);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore");
            }
          }}
          data-testid="regen-all-future-btn"
          className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"
          title="Reset di sicurezza: rigenera PIN per tutti gli arrivi futuri (esclusi ospiti già in casa)"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Rigenera tutti i PIN futuri
        </button>
        <button
          onClick={async () => {
            // Step 1: dry-run to preview
            let dry;
            try {
              const { data } = await client().post("/hk/ttlock/purge-orphans?dry_run=true");
              dry = data;
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore preview");
              return;
            }
            const revoke = dry.revoked || 0;
            const staffProtected = dry.preserved_by_name || 0;
            const dbProtected = dry.keep_count || 0;
            const perLock = (dry.locks || []).map(
              (l) => `  Camera ${l.room_number}: ${l.revoked} da revocare (${l.total_pwds} totali, ${l.kept + l.kept_by_name} tenuti)`
            ).join("\n");
            const ok = confirm(
              `PULIZIA ARCHIVI TTLOCK — DRY RUN\n\n` +
              `${revoke} codici orfani da revocare in totale.\n\n` +
              `PROTETTI:\n` +
              `• ${dbProtected} codici prenotazioni (in-casa + futuri)\n` +
              `• ${staffProtected} codici Staff (Francesco/Rodolfo/Elisa/Marco/…)\n\n` +
              `PER STANZA:\n${perLock}\n\n` +
              `⚠️ Confermando REVOCA DAVVERO i ${revoke} codici orfani su TTLock. L'operazione richiede alcuni minuti.\n\n` +
              `Procedere?`
            );
            if (!ok) return;
            try {
              toast.info(`Pulizia in corso di ${revoke} codici… richiede 3–5 minuti.`);
              const { data } = await client().post("/hk/ttlock/purge-orphans?dry_run=false");
              toast.success(
                `Pulizia completata: ${data.revoked} revocati, ${data.failed} falliti. ` +
                `Protetti: ${data.keep_count} prenotazioni + ${data.preserved_by_name} staff.`
              );
              setTimeout(load, 3000);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore purge");
            }
          }}
          data-testid="purge-ttlock-orphans-btn"
          className="rounded-full bg-red-700 hover:bg-red-800 text-white px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"
          title="Pulizia nucleare: revoca tutti i codici orfani sulle 7 serrature (mantiene ospiti in-casa + futuri + staff)"
        >
          <Trash2 className="h-3.5 w-3.5" /> Pulisci archivi TTLock
        </button>
      </div>
      {showAdd && (
        <AddReservationForm
          rooms={rooms}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : filteredRooms.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#7c5a4e]">Nessuna stanza da mostrare.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-[#faf7f2]">
          {filteredRooms.map((room) => (
            <RoomLockCard
              key={room.id}
              room={room}
              reservations={byRoom(room.id).filter(matchesQ)}
              today={today}
              onChange={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddReservationForm({ rooms, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    room_id: "",
    summary: "",
    guest_phone: "",
    guest_email: "",
    start: today,
    end: tomorrow,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.room_id) { toast.error("Seleziona una camera"); return; }
    if (!form.summary.trim()) { toast.error("Inserisci nome ospite"); return; }
    if (form.end <= form.start) { toast.error("Check-out deve essere dopo check-in"); return; }
    setSaving(true);
    try {
      const { data } = await client().post("/hk/reservations", form);
      const hasPin = data?.ttlock_pin;
      toast.success(
        hasPin
          ? `Prenotazione creata · PIN camera generato (${data.ttlock_pin})`
          : "Prenotazione creata",
      );
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione prenotazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="add-reservation-form"
      className="border-b border-[#e8dcc8] bg-[#fbf6ef] px-5 py-4"
    >
      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-[#7c5a4e]">
          Camera *
          <select
            value={form.room_id}
            onChange={set("room_id")}
            data-testid="add-res-room"
            required
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          >
            <option value="">— Seleziona —</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-[#7c5a4e]">
          Nome ospite *
          <input
            value={form.summary}
            onChange={set("summary")}
            data-testid="add-res-name"
            required
            placeholder="Es. Mario Rossi"
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-xs font-semibold text-[#7c5a4e]">
          Telefono
          <input
            value={form.guest_phone}
            onChange={set("guest_phone")}
            data-testid="add-res-phone"
            placeholder="+39 333 1234567"
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-xs font-semibold text-[#7c5a4e]">
          Email
          <input
            type="email"
            value={form.guest_email}
            onChange={set("guest_email")}
            data-testid="add-res-email"
            placeholder="ospite@example.com"
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-xs font-semibold text-[#7c5a4e]">
          Check-in (16:00)
          <input
            type="date"
            value={form.start}
            onChange={set("start")}
            data-testid="add-res-start"
            required
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-xs font-semibold text-[#7c5a4e]">
          Check-out (10:00)
          <input
            type="date"
            value={form.end}
            onChange={set("end")}
            data-testid="add-res-end"
            required
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="md:col-span-2 text-xs font-semibold text-[#7c5a4e]">
          Note (facoltative)
          <input
            value={form.notes}
            onChange={set("notes")}
            data-testid="add-res-notes"
            placeholder="Richieste speciali, orario di arrivo…"
            className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white"
          />
        </label>
      </div>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] text-[#7c5a4e]">
          Per le camere Argo · Bestla · Clizio · Damaseno viene generato automaticamente
          il PIN TTLock (valido 16:00 → 10:00) e l'ospite viene aggiunto al DB clienti.
        </p>
        <button
          type="submit"
          disabled={saving}
          data-testid="add-res-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Crea prenotazione
        </button>
      </div>
    </form>
  );
}

function _guestStatus(res, today) {
  if (!res) return { label: "Libera", cls: "bg-white text-[#7c5a4e] border border-[#e8dcc8]", icon: BedDouble };
  const start = new Date(res.start); start.setHours(0, 0, 0, 0);
  const end = new Date(res.end); end.setHours(0, 0, 0, 0);
  if (start.getTime() <= today.getTime() && end.getTime() > today.getTime()) {
    return { label: "In casa", cls: "bg-[#FAF3EE] text-[#4A1C17] border border-[#E8DCC8]", icon: Home };
  }
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  if (start.getTime() === today.getTime()) return { label: "Arriva oggi", cls: "bg-[#7C5A4E] text-[#F7EBDF]", icon: LogIn };
  if (start.getTime() === tomorrow.getTime()) return { label: "Arriva domani", cls: "bg-[#C5A059] text-white", icon: LogIn };
  if (end.getTime() === today.getTime()) return { label: "Parte oggi", cls: "bg-[#4A1C17] text-[#F7EBDF]", icon: LogOut };
  return { label: "In arrivo", cls: "bg-white text-[#4a1c17] border border-[#e8dcc8]", icon: LogIn };
}

function _fmtRange(res) {
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); }
    catch { return iso; }
  };
  return `${fmt(res.start)} → ${fmt(res.end)}`;
}

function RoomLockCard({ room, reservations, today, onChange }) {
  const [expanded, setExpanded] = useState(false);
  // Prioritise the guest that is currently in-house, else the next arrival.
  const inHouse = reservations.find((r) => {
    const s = new Date(r.start); s.setHours(0, 0, 0, 0);
    const e = new Date(r.end); e.setHours(0, 0, 0, 0);
    return s.getTime() <= today.getTime() && e.getTime() > today.getTime();
  });
  // When someone is in-house, surface the next arrival too so the admin can
  // pre-send that guest their code a few days early.
  const nextArrival = reservations.find((r) => {
    if (r.id === inHouse?.id) return false;
    const s = new Date(r.start); s.setHours(0, 0, 0, 0);
    return s.getTime() >= today.getTime();
  });
  const current = inHouse || nextArrival || reservations[0];
  const status = _guestStatus(current, today);
  const StatusIcon = status.icon;
  // "others" = everything not shown in the main slot AND not shown as
  // dedicated "next arrival" section.
  const surfaced = new Set([current?.id, inHouse ? nextArrival?.id : null].filter(Boolean));
  const others = reservations.filter((r) => !surfaced.has(r.id));
  const sync = lockSyncState(reservations);

  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden" data-testid={`room-lock-card-${room.id}`}>
      <div className="px-4 py-3 bg-gradient-to-br from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#f7ebdf]/10 flex items-center justify-center shrink-0">
          <BedDouble className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg font-bold leading-tight flex items-center gap-2">
            <span>{room.number ? `${room.number}. ` : ""}{room.name}</span>
            {room.number && (
            <span
              title={sync.title}
              data-testid={`room-sync-badge-${room.id}`}
              data-state={sync.state}
              className="inline-flex items-center gap-1 rounded-full bg-[#f7ebdf]/10 px-2 py-0.5 text-[10px] font-sans font-semibold tracking-wide"
            >
              <span className={`h-2 w-2 rounded-full ${sync.dot} ${sync.state === "misaligned" ? "animate-pulse" : ""}`} />
              {sync.label}
            </span>
            )}
          </div>
          <div className="text-[11px] text-[#f7ebdf]/70">
            {reservations.length} prenotazion{reservations.length === 1 ? "e" : "i"} in arrivo
          </div>
        </div>
        <button
          onClick={() => openTTLockApp(room.name)}
          title="Apri l'app TTLock (copia il nome serratura)"
          data-testid={`room-open-ttlock-${room.id}`}
          className="h-8 w-8 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/25 flex items-center justify-center shrink-0 transition-colors"
        >
          <Smartphone className="h-4 w-4" />
        </button>
        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold flex items-center gap-1 ${status.cls}`}>
          <StatusIcon className="h-3 w-3" /> {status.label}
        </span>
      </div>

      {current ? (
        <ReservationPinRow row={current} onChange={onChange} />
      ) : (
        <div className="px-4 py-6 text-center text-sm text-[#7c5a4e] italic" data-testid={`room-empty-${room.id}`}>
          Nessuna prenotazione in arrivo
        </div>
      )}

      {inHouse && nextArrival && (
        <div className="border-t border-[#e8dcc8] bg-[#FBF6E8]" data-testid={`room-next-arrival-${room.id}`}>
          <div className="px-4 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-[#7c5a4e] uppercase tracking-wide">
            <LogIn className="h-3 w-3 text-[#C5A059]" /> Prossimo arrivo
          </div>
          <ReservationPinRow row={nextArrival} onChange={onChange} />
        </div>
      )}

      {others.length > 0 && (
        <>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full px-4 py-2 border-t border-[#e8dcc8] bg-[#faf7f2] hover:bg-[#f7ebdf] text-[11px] text-[#7c5a4e] font-semibold flex items-center justify-center gap-1"
            data-testid={`room-toggle-others-${room.id}`}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expanded ? "Nascondi" : `Mostra ${others.length} altre prenotazioni`}
          </button>
          {expanded && (
            <div className="divide-y divide-[#e8dcc8] border-t border-[#e8dcc8]">
              {others.map((r) => (
                <ReservationPinRow key={r.id} row={r} onChange={onChange} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ReservationPinRow({ row, onChange }) {
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState(row.guest_phone || "");
  const [pollId, setPollId] = useState(null);

  useEffect(() => () => { if (pollId) clearInterval(pollId); }, [pollId]);

  const regen = async () => {
    const label = row.ttlock_pin ? "Rigenerare" : "Generare";
    if (!confirm(`${label} il codice serratura per ${row.summary || "questa prenotazione"}? L'operazione richiede 15-60 secondi.`)) return;
    setBusy(true);
    try {
      await client().post(`/hk/reservations/${row.id}/regen-pin`);
      toast.success("Generazione in corso — il codice comparirà tra 15-60 secondi");
      let tries = 0;
      const id = setInterval(async () => {
        tries += 1;
        onChange?.();
        if (tries >= 30) clearInterval(id);
      }, 3000);
      setPollId(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rigenerazione");
    } finally { setBusy(false); }
  };

  const savePhone = async () => {
    if (phone.trim() === (row.guest_phone || "")) { setEditingPhone(false); return; }
    setBusy(true);
    try {
      await client().patch(`/hk/reservations/${row.id}/phone`, { phone: phone.trim() });
      toast.success("Telefono aggiornato");
      setEditingPhone(false);
      onChange?.();
    } catch (e) {
      toast.error("Errore salvataggio telefono");
    } finally { setBusy(false); }
  };

  const sendWa = async () => {
    try {
      const { data } = await client().get(`/hk/reservations/${row.id}/wa`);
      openWhatsApp(data.wa_url);
      try {
        await client().post(`/hk/reservations/${row.id}/wa-mark`);
        onChange?.();
      } catch { /* silent */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore link WhatsApp");
    }
  };

  return (
    <div className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`reservation-pin-row-${row.id}`}>
      <div className="flex-1 min-w-[160px]">
        <div className="font-semibold text-[#2b1410] text-sm">
          {row.summary || "Prenotazione"}
        </div>
        <div className="text-[11px] text-[#7c5a4e]">{_fmtRange(row)}</div>
        {editingPhone ? (
          <div className="mt-1 flex items-center gap-1">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+39 333 1234567"
              className="rounded border border-[#e8dcc8] px-2 py-0.5 text-xs w-44"
              data-testid={`reservation-phone-input-${row.id}`}
              autoFocus
            />
            <button onClick={savePhone} disabled={busy} className="p-1 rounded bg-[#4a1c17] text-white"><Save className="h-3 w-3" /></button>
            <button onClick={() => { setPhone(row.guest_phone || ""); setEditingPhone(false); }} className="p-1 rounded border border-[#e8dcc8]"><X className="h-3 w-3" /></button>
          </div>
        ) : (
          <button
            onClick={() => setEditingPhone(true)}
            className="mt-0.5 text-[11px] text-[#7c5a4e] hover:text-[#4a1c17] inline-flex items-center gap-1"
            data-testid={`reservation-phone-${row.id}`}
            title="Clicca per modificare"
          >
            <Phone className="h-3 w-3" /> {row.guest_phone || "aggiungi numero"}
          </button>
        )}
      </div>
      <div className="font-mono text-base font-bold text-[#4a1c17] tracking-widest min-w-[100px] text-center">
        {row.ttlock_pin ? (
          <span className="inline-flex items-center gap-2">
            {row.ttlock_verified === true && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" title="PIN presente sulla serratura" data-testid={`reservation-verified-${row.id}`} />}
            {row.ttlock_verified === false && <ShieldAlert className="h-3.5 w-3.5 text-red-600" title="PIN NON presente sulla serratura — rigenera o invia WhatsApp" data-testid={`reservation-misaligned-${row.id}`} />}
            {showPin ? row.ttlock_pin : "••••••"}
            <button onClick={() => setShowPin((v) => !v)} className="text-[#7c5a4e] hover:text-[#4a1c17]">
              {showPin ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </span>
        ) : (
          <span className="text-xs font-normal text-[#7c5a4e]">nessun PIN</span>
        )}
      </div>
      <button
        onClick={regen}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-3 py-1 text-xs font-semibold disabled:opacity-40"
        data-testid={`reservation-regen-${row.id}`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
        {row.ttlock_pin ? "Rigenera" : "Genera"}
      </button>
      {row.ttlock_pin && (
        <button
          onClick={sendWa}
          className={`inline-flex items-center gap-1 rounded-full text-white px-3 py-1 text-xs font-semibold transition-colors ${
            row.wa_sent_at
              ? "bg-purple-600 hover:bg-purple-700"
              : "bg-green-600 hover:bg-green-700"
          }`}
          title={row.wa_sent_at ? `Inviato ${new Date(row.wa_sent_at).toLocaleString("it-IT")}` : "Invia PIN via WhatsApp"}
          data-testid={`reservation-wa-${row.id}`}
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {row.wa_sent_at ? "Inviato" : "WhatsApp"}
        </button>
      )}
    </div>
  );
}

function SpaLocksPanel() {
  const [status, setStatus] = useState(null);
  const [locks, setLocks] = useState([]);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [customPin, setCustomPin] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await client().get("/tuya/status");
      setStatus(s.data);
      const [l, p] = await Promise.all([
        s.data.configured ? client().get("/tuya/locks") : Promise.resolve({ data: { locks: [] } }),
        client().get("/tuya/spa/pins"),
      ]);
      setLocks(l.data.locks || []);
      setPins(p.data.pins || []);
    } catch (e) {
      // Fail silently — panel just shows "not configured" state.
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Poll while any pin is "creating" so the progress ticker animates.
  useEffect(() => {
    const anyBusy = pins.some((p) => p.status === "creating");
    if (!anyBusy) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [pins]);

  const createPin = async () => {
    setCreating(true);
    try {
      await client().post("/tuya/spa/pin", {
        name: name.trim() || "Spa PIN",
        keyboard_pwd: customPin || null,
      });
      toast.success("Creazione PIN Spa avviata su tutte le serrature");
      setName(""); setCustomPin("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.tuya?.msg || e.response?.data?.detail || "Errore");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (pin) => {
    if (!confirm(`Revocare PIN ${pin.pin} (${pin.name})? Il codice smetterà di aprire tutte le porte Spa.`)) return;
    try {
      await client().delete(`/tuya/spa/pin/${pin.id}`);
      toast.success("PIN Spa revocato");
      load();
    } catch (e) {
      toast.error("Revoca fallita");
    }
  };

  if (loading) return null;

  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden" data-testid="spa-locks-panel">
      <div className="px-5 py-3 border-b border-[#e8dcc8] flex items-center gap-2">
        <Key className="h-5 w-5 text-[#4a1c17]" />
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Serrature Spa (Tuya WiFi)</h3>
        <button
          onClick={() => setShowHistory(true)}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] text-[#7c5a4e] hover:bg-[#f8f1e5] px-3 py-1 text-xs"
          data-testid="spa-history-open"
          title="Storia PIN Spa"
        >
          <History className="h-3.5 w-3.5" /> Storia
        </button>
        <button
          onClick={async () => {
            let dry;
            try {
              const { data } = await client().post("/tuya/spa/purge-orphans?dry_run=true&only_expired=true");
              dry = data;
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore preview SPA");
              return;
            }
            const revoke = dry.revoked || 0;
            const active = dry.preserved_active || 0;
            const staff = dry.preserved_by_name || 0;
            const dbKept = dry.keep_count || 0;
            const perLock = (dry.locks || []).map(
              (l) => `  ${l.device_id.slice(-8)}: ${l.revoked} scaduti da revocare (${l.total_pwds} totali, ${l.kept + l.kept_by_name + l.kept_active} tenuti)`
            ).join("\n");
            if (revoke === 0) {
              toast.info("Nessun codice SPA scaduto orfano da revocare.");
              return;
            }
            const ok = confirm(
              `PULIZIA ARCHIVI SPA — DRY RUN\n\n` +
              `${revoke} codici SCADUTI orfani da revocare.\n\n` +
              `PROTETTI:\n` +
              `• ${dbKept} PIN attivi da DB (staff + prenotazioni)\n` +
              `• ${staff} codici Staff (per prefisso nome)\n` +
              `• ${active} codici ancora ATTIVI (scadenza futura)\n\n` +
              `PER SERRATURA:\n${perLock}\n\n` +
              `⚠️ Confermando REVOCA i ${revoke} codici scaduti sulle 3 serrature SPA.\n\n` +
              `Procedere?`
            );
            if (!ok) return;
            try {
              toast.info(`Pulizia SPA in corso… ~1 minuto.`);
              const { data } = await client().post("/tuya/spa/purge-orphans?dry_run=false&only_expired=true");
              toast.success(`SPA: ${data.revoked} revocati, ${data.failed} falliti (già scaduti lato Tuya).`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore purge SPA");
            }
          }}
          className="inline-flex items-center gap-1 rounded-full bg-red-700 hover:bg-red-800 text-white px-3 py-1 text-xs font-semibold"
          data-testid="spa-purge-orphans-btn"
          title="Pulizia SPA: revoca codici scaduti orfani (mantiene attivi + staff)"
        >
          <Trash2 className="h-3.5 w-3.5" /> Pulisci archivi SPA
        </button>
      </div>
      <div className="px-5 py-2 border-b border-[#e8dcc8] bg-white">
        <span className="text-[11px] text-[#7c5a4e]">1 PIN apre Spogliatoio · Anti Spa · Spa</span>
      </div>
      {showHistory && <SpaHistoryModal onClose={() => setShowHistory(false)} />}
      {!status?.configured ? (
        <div className="p-6 text-sm text-[#7c5a4e]">Tuya non configurato. Aggiungi le variabili TUYA_* in .env.</div>
      ) : (
        <>
          <div className="px-5 py-3 bg-[#faf7f2] border-b border-[#e8dcc8] text-sm">
            <span className="text-emerald-700 font-semibold">✓ Collegato · {locks.length} serrature</span>
            <span className="ml-3 text-[#7c5a4e]">
              {locks.map((l) => (
                <span key={l.id} className={`inline-flex items-center gap-1 mr-3 ${l.online ? "" : "text-red-700"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${l.online ? "bg-emerald-500" : "bg-red-400"}`} />
                  {l.name}
                </span>
              ))}
            </span>
          </div>

          <div className="p-4 border-b border-[#e8dcc8] grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
            <div className="sm:col-span-2">
              <label className="text-xs text-[#7c5a4e] block mb-1">Etichetta PIN</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Mario Rossi · 12/09" className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm" data-testid="spa-pin-name" />
            </div>
            <div>
              <label className="text-xs text-[#7c5a4e] block mb-1">
                PIN (7 cifre, opz.){status?.spa_pin_prefix ? ` — inizia con ${status.spa_pin_prefix}` : ""}
              </label>
              <input value={customPin} onChange={(e) => setCustomPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 7))} placeholder={status?.spa_pin_prefix ? `${status.spa_pin_prefix}${"x".repeat(Math.max(0, 7 - status.spa_pin_prefix.length))}` : "lascia vuoto per auto"} className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm font-mono" data-testid="spa-pin-custom" />
            </div>
            <button onClick={createPin} disabled={creating} className="inline-flex items-center justify-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="spa-pin-create">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Genera PIN Spa
            </button>
          </div>

          <div className="divide-y divide-[#e8dcc8]">
            {pins.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#7c5a4e]">Nessun PIN Spa attivo</div>
            ) : (
              pins.map((p) => <SpaPinRow key={p.id} pin={p} onRevoke={revoke} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SpaPinRow({ pin, onRevoke }) {
  const [show, setShow] = useState(false);
  const busy = pin.status === "creating";
  const partial = pin.status === "partial";
  const revoked = pin.status === "revoked";

  const badge = revoked
    ? { text: "Revocato", cls: "bg-neutral-200 text-neutral-700" }
    : busy
    ? { text: `Creazione ${pin.progress?.done ?? 0}/${pin.progress?.total ?? 0}`, cls: "bg-amber-100 text-amber-800" }
    : partial
    ? { text: "Errori parziali", cls: "bg-amber-100 text-amber-800" }
    : { text: "Attivo", cls: "bg-emerald-100 text-emerald-800" };

  return (
    <div className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`spa-pin-row-${pin.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-semibold text-[#2b1410]">{pin.name || "Spa PIN"}</div>
        <div className="text-[11px] text-[#7c5a4e]">
          {new Date(pin.created_at).toLocaleString("it-IT")}
          {pin.results?.length > 0 && (
            <span className="ml-2">
              {pin.results.map((r, i) => (
                <span key={i} className={r.ok ? "text-emerald-700" : "text-red-700"} title={r.ok ? "OK" : (r.error?.tuya?.msg || "err")}>
                  {r.ok ? "✓ " : "✗ "}
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
      <div className="font-mono text-lg font-bold text-[#4a1c17] tracking-widest min-w-[120px] text-center">
        {busy ? (
          <span className="inline-flex items-center gap-2 text-sm font-normal text-[#7c5a4e]">
            <Loader2 className="h-4 w-4 animate-spin" />
            {pin.progress?.done ?? 0}/{pin.progress?.total ?? 0}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            {show ? pin.pin : "•••••••"}
            <button onClick={() => setShow((v) => !v)} className="text-[#7c5a4e] hover:text-[#4a1c17]">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </span>
        )}
      </div>
      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${badge.cls}`}>{badge.text}</span>
      {!revoked && !busy && (
        <button onClick={() => onRevoke(pin)} className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-700 hover:bg-red-50 px-3 py-1 text-xs" data-testid={`spa-pin-revoke-${pin.id}`}>
          <Trash2 className="h-3.5 w-3.5" /> Revoca
        </button>
      )}
    </div>
  );
}

function StaffPanel({ staff, configured, onChange }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Inserisci un nome"); return; }
    setBusy(true);
    try {
      await client().post("/ttlock/staff", { name, phone: newPhone.trim() });
      toast.success(`${name} aggiunto/a`);
      setNewName(""); setNewPhone(""); setAdding(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden" data-testid="locks-staff-panel">
      <div className="px-5 py-3 border-b border-[#e8dcc8] flex items-center gap-2 flex-wrap">
        <Users className="h-5 w-5 text-[#4a1c17]" />
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Codici Personale (permanenti)</h3>
        <span className="text-[11px] text-[#7c5a4e] hidden md:inline">Aperti su tutte le 7 camere · rotazione con revoca del vecchio</span>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-3 py-1 text-xs font-semibold"
            data-testid="staff-add-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Aggiungi membro
          </button>
        )}
      </div>
      {adding && (
        <div className="px-4 py-3 border-b border-[#e8dcc8] bg-[#faf7f2] flex flex-wrap items-end gap-2" data-testid="staff-add-form">
          <label className="text-xs text-[#7c5a4e] flex-1 min-w-[140px]">
            Nome
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="es. Mario Rossi"
              className="mt-1 w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm"
              data-testid="staff-add-name"
            />
          </label>
          <label className="text-xs text-[#7c5a4e] flex-1 min-w-[140px]">
            Telefono
            <input
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+39 333 1234567"
              className="mt-1 w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm"
              data-testid="staff-add-phone"
            />
          </label>
          <button
            onClick={create}
            disabled={busy}
            className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-1.5 text-sm font-semibold disabled:opacity-40"
            data-testid="staff-add-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiungi"}
          </button>
          <button
            onClick={() => { setAdding(false); setNewName(""); setNewPhone(""); }}
            className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-sm"
          >
            Annulla
          </button>
        </div>
      )}
      {staff.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#7c5a4e]">Nessun ruolo configurato — clicca "Aggiungi membro"</div>
      ) : (
        <div className="divide-y divide-[#e8dcc8]">
          {staff.map((s) => (
            <StaffRow key={s.id} row={s} configured={configured} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function StaffRow({ row, configured, onChange }) {
  const [showPin, setShowPin] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [phone, setPhone] = useState(row.phone || "");
  const [name, setName] = useState(row.name || "");
  const [customPin, setCustomPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const rotating = row.status === "rotating";
  const errored = row.status === "error";

  // Poll while rotation is in-flight so the progress counter animates.
  useEffect(() => {
    if (!rotating) return;
    const t = setInterval(() => onChange?.(), 3000);
    return () => clearInterval(t);
  }, [rotating, onChange]);

  const savePhone = async () => {
    if (phone.trim() === (row.phone || "")) { setEditingPhone(false); return; }
    setBusy(true);
    try {
      await client().patch(`/ttlock/staff/${row.id}`, { phone });
      toast.success("Telefono aggiornato");
      setEditingPhone(false);
      onChange?.();
    } catch (e) {
      toast.error("Errore aggiornamento telefono");
    } finally { setBusy(false); }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Il nome non può essere vuoto"); return; }
    if (trimmed === (row.name || "")) { setEditingName(false); return; }
    setBusy(true);
    try {
      await client().patch(`/ttlock/staff/${row.id}`, { name: trimmed });
      toast.success("Nome aggiornato");
      setEditingName(false);
      onChange?.();
    } catch (e) {
      toast.error("Errore aggiornamento nome");
    } finally { setBusy(false); }
  };

  const rotate = async (pin) => {
    if (!configured) { toast.error("TTLock non configurato"); return; }
    if (row.pin && !confirm(`Sostituire il PIN corrente di ${row.name}? Il vecchio verrà revocato e il nuovo attivato su tutte le serrature (30-90 secondi).`)) return;
    setBusy(true);
    try {
      await client().post(`/ttlock/staff/${row.id}/pin`, { keyboard_pwd: pin || null });
      toast.success(`Rotazione avviata — aggiornamento in corso su tutte le serrature`);
      setCustomPin("");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.ttlock?.errmsg || e.response?.data?.detail || "Errore rotazione PIN");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!confirm(`Revocare il PIN di ${row.name}? Il codice smetterà di funzionare su tutte le serrature.`)) return;
    setBusy(true);
    try {
      await client().delete(`/ttlock/staff/${row.id}/pin`);
      toast.success("PIN revocato");
      onChange?.();
    } catch (e) {
      toast.error("Errore revoca");
    } finally { setBusy(false); }
  };

  const sendWa = async () => {
    try {
      const { data } = await client().get(`/ttlock/staff/${row.id}/wa`);
      openWhatsApp(data.wa_url);
      try {
        await client().post(`/ttlock/staff/${row.id}/wa-mark`);
        onChange?.();
      } catch { /* silent */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore link WhatsApp");
    }
  };

  const deleteMember = async () => {
    if (!confirm(`Eliminare ${row.name} dalla lista? Il PIN verrà revocato da tutte le serrature e la persona sparirà definitivamente.`)) return;
    setBusy(true);
    try {
      await client().delete(`/ttlock/staff/${row.id}`);
      toast.success(`${row.name} eliminato/a`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    } finally { setBusy(false); }
  };

  const progress = row.progress || {};
  return (
    <div className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`staff-row-${row.id}`}>
      <div className="flex-1 min-w-[180px]">
        {editingName ? (
          <div className="flex items-center gap-1">
            <input value={name} onChange={(e) => setName(e.target.value)} className="rounded border border-[#e8dcc8] px-2 py-1 text-sm w-56 font-semibold" data-testid={`staff-name-input-${row.id}`} autoFocus />
            <button onClick={saveName} disabled={busy} className="p-1 rounded bg-[#4a1c17] text-white"><Save className="h-3.5 w-3.5" /></button>
            <button onClick={() => { setName(row.name || ""); setEditingName(false); }} className="p-1 rounded border border-[#e8dcc8]"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setEditingName(true)} className="font-semibold text-[#2b1410] hover:text-[#4a1c17] text-left" data-testid={`staff-name-${row.id}`} title="Clicca per cambiare addetto">
            {row.name}
          </button>
        )}
        {editingPhone ? (
          <div className="mt-1 flex items-center gap-1">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded border border-[#e8dcc8] px-2 py-1 text-xs w-40" data-testid={`staff-phone-input-${row.id}`} autoFocus />
            <button onClick={savePhone} disabled={busy} className="p-1 rounded bg-[#4a1c17] text-white"><Save className="h-3.5 w-3.5" /></button>
            <button onClick={() => { setPhone(row.phone || ""); setEditingPhone(false); }} className="p-1 rounded border border-[#e8dcc8]"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setEditingPhone(true)} className="text-xs text-[#7c5a4e] hover:text-[#4a1c17] inline-flex items-center gap-1" title="Clicca per cambiare numero">
            <Phone className="h-3 w-3" /> {row.phone || "— nessun numero —"}
          </button>
        )}
        {errored && row.last_error && (
          <div className="text-[11px] text-red-700 mt-0.5">
            Errore: {typeof row.last_error === "string" ? row.last_error : (row.last_error?.ttlock?.errmsg || "riprova")}
          </div>
        )}
      </div>
      <div className="font-mono text-lg font-bold text-[#4a1c17] tracking-widest min-w-[110px] text-center">
        {rotating ? (
          <span className="inline-flex items-center gap-2 text-sm font-normal text-[#7c5a4e]">
            <Loader2 className="h-4 w-4 animate-spin" />
            {progress.total ? `${progress.done}/${progress.total}` : "..."}
          </span>
        ) : row.pin ? (
          <div className="flex flex-col items-center">
            <span className="inline-flex items-center gap-2">
              {showPin ? row.pin : "••••"}
              <button onClick={() => setShowPin((v) => !v)} className="text-[#7c5a4e] hover:text-[#4a1c17]">
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </span>
            {row.spa_pin && (
              <span className="text-[10px] font-normal tracking-normal text-[#7c5a4e] mt-0.5">
                Spa: <span className="font-mono font-bold text-[#4a1c17]">{showPin ? row.spa_pin : "•••••••"}</span>
                <span className="ml-1 text-[9px] italic">(7 cifre + #)</span>
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs font-normal text-[#7c5a4e]">nessun PIN</span>
        )}
      </div>
      <input
        value={customPin}
        onChange={(e) => setCustomPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
        placeholder="PIN 4-6 cifre"
        disabled={rotating}
        className="rounded border border-[#e8dcc8] px-2 py-1 text-sm w-28 disabled:opacity-40"
        data-testid={`staff-custom-pin-${row.id}`}
      />
      <button
        onClick={() => rotate(customPin)}
        disabled={busy || !configured || rotating}
        className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-3 py-1 text-xs font-semibold disabled:opacity-40"
        data-testid={`staff-rotate-${row.id}`}
      >
        {busy || rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
        {row.pin ? "Cambia" : "Genera"}
      </button>
      {row.pin && !rotating && (
        <>
          <button
            onClick={sendWa}
            className={`inline-flex items-center gap-1 rounded-full text-white px-3 py-1 text-xs font-semibold transition-colors ${
              row.wa_sent_at
                ? "bg-purple-600 hover:bg-purple-700"
                : "bg-green-600 hover:bg-green-700"
            }`}
            title={row.wa_sent_at ? `Inviato ${new Date(row.wa_sent_at).toLocaleString("it-IT")}` : "Invia PIN via WhatsApp"}
            data-testid={`staff-wa-${row.id}`}
          >
            <MessageCircle className="h-3.5 w-3.5" /> {row.wa_sent_at ? "Inviato" : "WhatsApp"}
          </button>
          <button onClick={revoke} className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-700 hover:bg-red-50 px-3 py-1 text-xs">
            <Trash2 className="h-3.5 w-3.5" /> Revoca
          </button>
        </>
      )}
      <button
        onClick={() => setShowHistory(true)}
        className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] text-[#7c5a4e] hover:bg-[#f8f1e5] px-3 py-1 text-xs"
        data-testid={`staff-history-${row.id}`}
        title="Storico cambi PIN"
      >
        <History className="h-3.5 w-3.5" /> Storia
      </button>
      <button
        onClick={deleteMember}
        disabled={busy || rotating}
        className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-700 hover:bg-red-50 px-2.5 py-1 text-xs disabled:opacity-40"
        data-testid={`staff-delete-${row.id}`}
        title="Elimina membro"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      {showHistory && <HistoryModal staffId={row.id} staffName={row.name} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function SpaHistoryModal({ onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    client().get("/tuya/spa/history")
      .then((r) => setRows(r.data.history || []))
      .catch(() => setRows([]));
  }, []);
  const label = {
    create: { text: "Creato", cls: "bg-green-100 text-green-800" },
    revoke: { text: "Revocato", cls: "bg-red-100 text-red-800" },
    create_partial: { text: "Creazione parziale", cls: "bg-amber-100 text-amber-800" },
  };
  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="spa-history-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#e8dcc8] flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg font-bold text-[#2b1410]">Storia PIN Spa</h3>
            <p className="text-[11px] text-[#7c5a4e]">Ultimi 100 eventi</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#f8f1e5]"><X className="h-4 w-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">
          {rows === null ? (
            <div className="py-8 text-center text-sm text-[#7c5a4e]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento...</div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#7c5a4e]">Nessun evento registrato</div>
          ) : (
            <ul className="space-y-2">
              {rows.map((h) => {
                const l = label[h.action] || { text: h.action, cls: "bg-gray-100 text-gray-800" };
                return (
                  <li key={h.id} className="rounded-xl border border-[#e8dcc8] bg-[#faf7f2] p-3 text-sm" data-testid={`spa-history-row-${h.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${l.cls}`}>{l.text}</span>
                      <span className="text-[11px] text-[#7c5a4e]">{fmt(h.at)}</span>
                      <span className="ml-auto text-[11px] text-[#7c5a4e]">{h.lock_ids?.length || 0} porte</span>
                    </div>
                    <div className="mt-1 text-[#2b1410] font-mono text-sm">
                      {h.pin && <span className="font-bold">{h.pin}</span>}
                      {h.name && <span className="ml-2 font-normal text-[#7c5a4e]">· {h.name}</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ staffId, staffName, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    client().get(`/ttlock/staff/${staffId}/history`)
      .then((r) => setRows(r.data.history || []))
      .catch(() => setRows([]));
  }, [staffId]);
  const label = {
    rotate: { text: "Generato / Cambiato", cls: "bg-green-100 text-green-800" },
    revoke: { text: "Revocato", cls: "bg-red-100 text-red-800" },
    rotate_failed: { text: "Rotazione fallita", cls: "bg-amber-100 text-amber-800" },
  };
  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="staff-history-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#e8dcc8] flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg font-bold text-[#2b1410]">Storia PIN · {staffName}</h3>
            <p className="text-[11px] text-[#7c5a4e]">Traccia audit degli ultimi 50 cambi</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#f8f1e5]"><X className="h-4 w-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">
          {rows === null ? (
            <div className="py-8 text-center text-sm text-[#7c5a4e]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento...</div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#7c5a4e]">Nessun evento registrato — il PIN non è mai stato cambiato.</div>
          ) : (
            <ul className="space-y-2">
              {rows.map((h) => {
                const l = label[h.action] || { text: h.action, cls: "bg-gray-100 text-gray-800" };
                return (
                  <li key={h.id} className="rounded-xl border border-[#e8dcc8] bg-[#faf7f2] p-3 text-sm" data-testid={`history-row-${h.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${l.cls}`}>{l.text}</span>
                      <span className="text-[11px] text-[#7c5a4e]">{fmt(h.at)}</span>
                      {h.locks_count ? <span className="ml-auto text-[11px] text-[#7c5a4e]">{h.locks_count} serrature</span> : null}
                    </div>
                    <div className="mt-1 text-[#2b1410] font-mono text-sm">
                      {h.old_pin && <span className="line-through text-[#7c5a4e]">{h.old_pin}</span>}
                      {h.old_pin && h.new_pin && <span className="mx-2">→</span>}
                      {h.new_pin && <span className="font-bold">{h.new_pin}</span>}
                      {h.extra?.spa_pin && <span className="ml-2 text-xs text-[#7c5a4e]">(Spa: <span className="font-bold text-[#4a1c17]">{h.extra.spa_pin}</span>)</span>}
                      {!h.old_pin && !h.new_pin && <span className="italic text-[#7c5a4e]">—</span>}
                    </div>
                    {h.phone_at_time && <div className="text-[11px] text-[#7c5a4e] mt-0.5">Tel. registrato: {h.phone_at_time}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#7c5a4e] uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
